import { Steps } from "antd";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useNavigate } from "react-router-dom";
import { MdArrowForward, MdSentimentSatisfiedAlt, MdStar } from "react-icons/md";
import Float from "@Components/Decor/Float";
import Skewer from "@Components/Decor/Skewer";
import { useSelector } from "react-redux";
import { RootState } from "src/store";
import "./HeroCard.css";

const HERO_STEPS = (t: TFunction) => [
  { title: t("UserHistory.ordersList.heroCard.redeemed") },
  { title: t("UserHistory.ordersList.heroCard.confirmed") },
  {
    title: t("UserHistory.ordersList.heroCard.enjoy"),
    icon: (
      <span className="border-border-active text-font-active flex size-6 items-center justify-center rounded-full border-2 border-dashed">
        <MdSentimentSatisfiedAlt className="size-3.5" />
      </span>
    ),
  },
];

export default function HeroCard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userName = useSelector((state: RootState) => state.auth.user?.Name);

  return (
    <div className="orders-hero-gradient relative isolate flex w-full flex-col gap-10 overflow-hidden px-5 py-10 md:flex-row md:items-center md:justify-between md:px-20">
      <div className="relative z-10 flex flex-col gap-4">
        <div className="bg-background-primary-lighter text-font-active flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium">
          <span className="size-1.5 rounded-full bg-current" />
          {t("UserHistory.ordersList.hero.badge")}
        </div>
        {userName && (
          <div className=" capitalize flex items-center text-2xl gap-1">
            <div>{t("UserHistory.ordersList.hero.greeting")}</div><div className="capitalize text-font-active  font-semibold">{userName}</div>
          </div>
        )}
        <div className="text-font-default text-3xl font-bold">{t("UserHistory.ordersList.hero.title")}</div>
        <div className="text-font-subtle flex flex-wrap items-center gap-2 text-sm">
          {t("UserHistory.ordersList.hero.subtitle")}
          <Skewer angle={3} className="font-bold! px-2 py-1">
            {t("UserHistory.ordersList.hero.highlight")}
          </Skewer>
        </div>
      </div>
      <div className="orders-hero-decor-scroll pointer-events-none absolute end-5 top-6 z-10 h-20 w-28 shrink-0 md:pointer-events-auto md:relative md:end-auto md:top-auto md:h-auto md:w-96">
        <div className="scale-30 absolute end-0 top-0 flex w-96 origin-top-right flex-col gap-4 md:relative md:scale-100 rtl:origin-top-left">
          <Float duration={5}>
            <div className="bg-background-default shadow-card flex w-full rotate-2 flex-col gap-4 rounded-2xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex flex-col gap-1">
                  <div className="text-font-default text-base font-semibold">{t("UserHistory.ordersList.heroCard.title")}</div>
                  <div className="text-font-subtle text-sm">{t("UserHistory.ordersList.heroCard.subtitle")}</div>
                </div>
                <div className="bg-background-success text-font-success flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-sm font-semibold">
                  <span className="size-1.5 rounded-full bg-current" />
                  {t("UserHistory.ordersList.heroCard.confirmed")}
                </div>
              </div>
              <Steps current={2} size="small" titlePlacement="vertical" items={HERO_STEPS(t)} />
            </div>
          </Float>
          <div className="self-end">
            <Float duration={3.5} delay={-0.6} distance={-6}>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="bg-background-active text-font-inverted flex -rotate-3 cursor-pointer items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold"
              >
                {t("UserHistory.ordersList.hero.cta")}
                <MdArrowForward className="size-4 rtl:rotate-180" />
              </button>
            </Float>
          </div>
        </div>
      </div>
      <span className="text-icon-active pointer-events-none absolute end-20 top-6">
        <Float duration={4} delay={-1.2} distance={-6}>
          <MdStar className="size-4" />
        </Float>
      </span>
      <span className="end-110 pointer-events-none absolute bottom-16 z-20 hidden md:block">
        <Float duration={4.5} delay={-3}>
          <span className="bg-background-active text-icon-inverted flex size-7 items-center justify-center rounded-full">
            <MdStar className="size-4" />
          </span>
        </Float>
      </span>
    </div>
  );
}
